
import PropTypes from 'prop-types';
import moment from "moment";
import axios from "axios";
import qs from 'qs';
import _, { isEmpty } from 'lodash';
import React, { Component } from 'react';

export const ListContext = React.createContext();

export class ListProvider extends Component {

  ORDER_ASC = 'asc';
  ORDER_DESC = 'desc';

  static defaultProps = {
    keyAttribute: 'id',
    filterInitial: {},
    filterDefaults: {},
    filterDefaultValues: {},
    removeFilter: [],
    setFilter: undefined,
    preload: true
  };

  static propTypes = {
    url: PropTypes.string,
    filterDefaults: PropTypes.object,
    orderDefaults: PropTypes.object,
    filterInitial: PropTypes.object,
    filterDefaultValues: PropTypes.object,
    storageKey: PropTypes.string,
    keyAttribute: PropTypes.string,
    removeFilter: PropTypes.array,
    onComplete: PropTypes.func,
    preload: PropTypes.bool
  };

  constructor(props) {
    super(props);

    this._isMounted = false;
    this._cancelSource = null;

    this.state = {
      url: props.url,
      loading: true,
      total: 0,
      pagination: null,
      pageNumber: 1,
      filter: {},
      items: [],
      response: null,
      permissions: {},
      storageKey: props.storageKey || `ListProvider-${props.url}`,
      selectedItems: [],
      filterErrors: {},
      order: {
        field: null,
        type: this.ORDER_ASC
      }
    };

    this.state.filter = this.getFilterDefaults();
    this.state.pageNumber = this.getPageNumberDefaults();
    this.state.order = this.getOrderDefaults();

    this.setFilter = this.setFilter.bind(this);
    this.updateItem = this.updateItem.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    const { preload } = this.props;
    if (preload) {
      if (this.props.setFilter) {
        this.setFilter(this.props.setFilter);
      } else {
        this.loadResults(this.state.pageNumber);
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._cancelSource) {
      this._cancelSource.cancel();
      this._cancelSource = null;
    }
  }

  getListCancelToken() {
    if (this._cancelSource) {
      this._cancelSource.cancel();
    }
    this._cancelSource = axios.CancelToken.source();
    return this._cancelSource.token;
  }

  loadResults(pageNumber) {
    this.setState({loading: true, filterErrors: {}});
    const { removeFilter, onComplete } = this.props;
    let params = {
      ...{page: pageNumber},
      ...this.props.filterInitial,
      ...this.state.filter,
      ...this.props.filterDefaults,
      ...this.getOrderParams()
    };
    removeFilter.forEach(name => {
      delete params[name];
    });
    axios.get(this.state.url, {
      params: params,
      paramsSerializer: function (params) {
        return qs.stringify(params, {arrayFormat: 'repeat'})
      },
      cancelToken: this.getListCancelToken(),
    })
    .then(res => {
      if (!this._isMounted) {
        return;
      }
      const data = res.data;

      this.setState({
        response: data,
        items: data.results,
        pagination: data.pagination,
        pageNumber: data.pagination ? data.pagination.page_number : 1,
        permissions: data.permissions ? data.permissions : {},
        total: data.total,
        loading: false
      }, () => {
        if (onComplete) {
          onComplete(this, this.state);
        }
      });

      localStorage.setItem(`${this.state.storageKey}-page`, data.pagination ? data.pagination.page_number : 1);
    })
    .catch(error => {
      if (axios.isCancel(error) || !this._isMounted) {
        return;
      }
      const response = error.response;

      let filterErrors = {}
      if (response && response.status === 404 && this.state.pageNumber !== 1) {
        this.setPageNumber(1);
      }
      else if (response && response.status === 400) {
        if (response.data) {
          filterErrors = response.data;
        }
      }

      this.setState({
        response: null,
        filterErrors: filterErrors,
        loading: false
      });
    })
  }

  getUrlFilter = () => {
    let params = {
      ...this.props.filterInitial,
      ...this.state.filter,
      ...this.props.filterDefaults,
      ...this.getOrderParams()
    };
    return qs.stringify(params, {arrayFormat: 'repeat'});
  }

  loadNext = () => {
    const { pagination } = this.state;
    this.setState({loading: true});
    if (pagination.next_link) {
      axios.get(pagination.next_link, {
        cancelToken: this.getListCancelToken(),
      })
        .then(res => {
          if (!this._isMounted) {
            return;
          }
          const data = res.data;
          this.setState({
            response: data,
            items: this.state.items.concat(data.results),
            pagination: data.pagination,
            pageNumber: data.pagination ? data.pagination.page_number : 1,
            permissions: data.permissions ? data.permissions : {},
            total: data.total,
            loading: false
          });
        })
        .catch(error => {
        });
    }
  };

  oneTimeLoadResults = (filter) => {
    this.setState({loading: true});
    axios.get(this.state.url, {params: filter, cancelToken: this.getListCancelToken()})
      .then(res => {
        if (!this._isMounted) {
          return;
        }
        const data = res.data;
        this.setState({
          response: data,
          items: data.results,
          loading: false
        });
      })
      .catch(error => {
      });
  }

  setFilter = (values, loadResults=true) => {
    if (values === undefined) {
      values = {};
    }

    this.setState({
      filter: values,
      loading: loadResults
    }, () => {
      if (loadResults) {
        this.loadResults(1);
      }
    });

    try {
      localStorage.setItem(`${this.state.storageKey}-filter`, JSON.stringify(values));
    }
    catch (e) {}
  };

  hasFilter = () => {
    return isEmpty(this.state.filter);
  };

  setFilterValue = (key, value) => {
    const { filterInitial } = this.props;
    let filter = this.state.filter;
    if (value === null || value === undefined) {
      if (filterInitial[key]) {
        filter[key] = filterInitial[key];
      }
      else {
        delete filter[key];
      }
    }
    else {
      filter[key] = value;
    }
    this.setFilter(filter);
  };

  setFilterValues = (values) => {
    let { filter } = this.state;
    Object.keys(values).forEach(name => {
      _.set(filter, name, values[name]);
    });
    this.setFilter(filter);
  };

  setFilterDateValue = (name, value) => {
   let filter = this.state.filter;
    if (value && moment(value).isValid()) {
      _.set(filter, name, moment(value).format('YYYY-MM-DD'));
    }
    else {
      _.set(filter, name, null);
    }
    this.setFilter(filter);
  };

  getFilterDefaults = () => {
    const { filterInitial, filterDefaultValues } = this.props;
    let value = localStorage.getItem(`${this.state.storageKey}-filter`);
    let filterData = {};
    if (value) {
      try {
        filterData = JSON.parse(value);
      }
      catch (e) {}
    }

    for (const [key, value] of Object.entries(filterDefaultValues)) {
      if (filterData[key] === undefined) {
        filterData[key] = value;
      }
    }

    for (const [key, value] of Object.entries(filterInitial)) {
      if (filterData[key] === null || filterData[key] === undefined) {
        filterData[key] = value;
      }
    }

    return filterData;
  };

  getOrderDefaults = () => {
    const { orderDefaults } = this.props;
    let value = localStorage.getItem(`${this.state.storageKey}-order`);
    let orderData = {};
    if (value) {
      try {
        orderData = JSON.parse(value);
      }
      catch (e) {}
    }
    if (_.isEmpty(orderData) && orderDefaults) {
      orderData = orderDefaults;
    }
    return orderData;
  };

  setPageNumber = (number) => {
    this.loadResults(number);
  };

  getPageNumberDefaults = () => {
    let page = null;
    try {
      page = parseInt(localStorage.getItem(`${this.state.storageKey}-page`));
    }
    catch (e) {}
    if (!page || typeof page !== 'number') {
      page = 1;
    }
    else if (page <= 0) {
      page = 1;
    }
    return page;
  };

  updateItem = (item, push = true) => {
    let items = this.state.items;
    const index = items.findIndex((element) => {
      return element.id === item.id;
    });
    if (index !== -1) {
      items[index] = item;
    }
    else if (push) {
      items.push(item);
    }

    this.setState({
      items: items
    });
  };

  updateItemArrayValue = (id, name, value) => {
    let items = this.state.items;
    const index = items.findIndex((element) => {
      return element.id === id;
    });
    if (index !== -1) {
      let item = items[index];
      let itemArrayIndex = _.findIndex(item[name], { id: value.id});
      if (itemArrayIndex !== -1) {
        _.set(items, `[${index}].${name}.[${itemArrayIndex}]`, value);
        this.setState({
          items: items
        })
      }
    }
  };

  findIndex = (item) => {
    let items = this.state.items;
    return  items.findIndex((element) => {
      return element.id === item.id;
    });
  };

  addItem = (item, position='start') => {
    let { items } = this.state;
    if (position === 'start') {
      items.unshift(item);
    }
    else if (position === 'end') {
      items.push(item);
    }
    this.setState({
      items: items
    });
  };

  deleteItem = (item) => {
    let items = this.state.items;
    const index = items.findIndex((element) => {
      return element.id === item.id;
    });
    if (index !== -1) {
      items.splice(index, 1);
    }
    this.setState({
      items: items
    });
  };

  deleteItemByIndex = (index) => {
    let items = this.state.items;
    items.splice(index, 1);
    this.setState({
      items: items
    });
  };

  getOrderParams = () => {
    const order = this.state.order;
    if (order.field) {
      const type = order.type === this.ORDER_ASC ? '' : '-';
      return {
        o: `${type}${order.field}`
      }
    }
  };

  patchItem = (item, data) => {
    axios.patch(item.meta.url, data).then(res => {
      if (this._isMounted) {
        this.updateItem(res.data);
      }
    })
  };

  setOrder = (name, type) => {
    let order = this.state.order;
    if (order.field === name) {
      if (order.type === this.ORDER_DESC) {
        order.field = null;
        order.type = this.ORDER_ASC;
      }
      else {
        order.type = type === this.ORDER_ASC ? this.ORDER_DESC : this.ORDER_ASC;
      }
    }
    else {
      order.field = name;
      order.type = this.ORDER_ASC;
    }

    this.setState({
      order: order
    });

    localStorage.setItem(`${this.state.storageKey}-order`, JSON.stringify(order));

    this.setPageNumber(1);
  };

  getOrderField = (name) => {
    const order = this.state.order;
    if (order.field === name) {
      return order.type;
    }
    return null;
  };

  reload = () => {
    this.loadResults(this.state.pageNumber);
  };

  getItemKey = (item) => {
    const { keyAttribute } = this.props;
    return item[keyAttribute];
  }

  toggleSelectItem = (item) => {
    let { selectedItems } = this.state;
    const itemKey = this.getItemKey(item);
    if (selectedItems.includes(itemKey)) {
      selectedItems.splice(selectedItems.indexOf(itemKey), 1);
    }
    else {
      selectedItems.push(itemKey);
    }
    this.setState({
      selectedItems: selectedItems
    })
  };

  selectItemByKey = (itemKey) => {
    let { selectedItems } = this.state;
    if (!selectedItems.includes(itemKey)) {
      selectedItems.push(itemKey);
    }
    this.setState({
      selectedItems: selectedItems
    })
  }

  isItemSelected = (item) => {
    const { selectedItems } = this.state;
    return selectedItems.includes(this.getItemKey(item));
  };

  selectAllItems = () => {
    const { items } = this.state;
    let selectedItems = [];
    if (!this.isSelectedAllItems()) {
      items.forEach(item => selectedItems.push(this.getItemKey(item)));
    }
    this.setState({
      selectedItems: selectedItems
    });
  };

  isSelectedAllItems = () => {
    const { items, selectedItems } = this.state;
    return items.length > 0 && items.length === selectedItems.length;
  };

  getSelectedItems = () => {
    const { items, selectedItems } = this.state;
    return items.filter((item) => selectedItems.includes(this.getItemKey(item)));
  };

  hasSelectedItems = () => {
    const { selectedItems } = this.state;
    return selectedItems.length > 0;
  }

  unselectAllItems = () => {
    this.setState({
      selectedItems: []
    })
  }

  getFilterError = (name) => {
    const errors = _.get(this.state.filterErrors, name)
    if (errors)
      return errors.join('. ');
  }

  hasFilterError = (name) => {
    return _.get(this.state.filterErrors, name) !== undefined;
  }

  hasFilterErrors = () => {
    return Object.keys(this.state.filterErrors).length > 0
  }

  render() {
    return (
      <ListContext.Provider value={{
        url: this.state.url,
        items: this.state.items,
        response: this.state.response,
        permissions: this.state.permissions,
        total: this.state.total,
        loading: this.state.loading,
        pagination: this.state.pagination,
        filter: this.state.filter,
        setFilter: this.setFilter,
        setFilterDateValue: this.setFilterDateValue,
        setFilterValue: this.setFilterValue,
        setFilterValues: this.setFilterValues,
        hasFilter: this.hasFilter,
        deleteItem: this.deleteItem,
        deleteItemByIndex: this.deleteItemByIndex,
        addItem: this.addItem,
        updateItem: this.updateItem,
        updateItemArrayValue: this.updateItemArrayValue,
        setPageNumber: this.setPageNumber,
        getOrderField: this.getOrderField,
        setOrder: this.setOrder,
        loadResults: this.loadResults,
        oneTimeLoadResults: this.oneTimeLoadResults,
        patchItem: this.patchItem,
        loadNext: this.loadNext,
        findIndex: this.findIndex,
        reload: this.reload,
        toggleSelectItem: this.toggleSelectItem,
        isItemSelected: this.isItemSelected,
        selectAllItems: this.selectAllItems,
        getSelectedItems: this.getSelectedItems,
        isSelectedAllItems: this.isSelectedAllItems,
        hasSelectedItems: this.hasSelectedItems,
        unselectAllItems: this.unselectAllItems,
        selectItemByKey: this.selectItemByKey,
        getOrderParams: this.getOrderParams,
        getUrlFilter: this.getUrlFilter,
        getFilterError: this.getFilterError,
        hasFilterError: this.hasFilterError,
        filterErrors: this.filterErrors,
        hasFilterErrors: this.hasFilterErrors,
      }}>
        {this.props.children}
      </ListContext.Provider>
    );
  }
}
